import { z } from 'zod';

import type { Address } from './address';

enum PROPERTY_TYPES {
  SELECT = 'select',
  TEXT = 'text',
  NUMERIC = 'numeric',
  CHECKBOX = 'checkbox',
  TEXTAREA = 'textarea',
  MULTISELECT = 'multiselect',
}

type PropertyValuesRecord = Record<string, string[]>;

export const ProductPropertiesSchema = z.object({
  dependsOn: z.string().nullable(),
  displayText: z.string(),
  key: z.string(),
  required: z.boolean(),
  insurance: z.string().optional(),
  state: z.string().optional(),
  type: z.enum(PROPERTY_TYPES),
  validationType: z.string().nullable(),
  values: z.union([z.array(z.string()), z.record(z.string(), z.array(z.string()))]),
  initialValue: z.string().optional(),
});

export type ProductProperties = {
  key: string;
  type: PROPERTY_TYPES;
  dependsOn: string | null;
  displayText: string;
  required: boolean;
  validationType: string | null;
  values: string[] | PropertyValuesRecord;
  category: string;
  subcategory: string | null;
  initialValue?: string;
  insurance?: string;
  state?: string;
};

export const ProductCategoriesSchema = z.object({
  categoryName: z.string(),
  parentCategory: z
    .object({
      categoryName: z.string(),
    })
    .nullable()
    .optional(),
});
export type ProductCategories = z.infer<typeof ProductCategoriesSchema>;

export const ProductImagesSchema = z.object({
  square: z.string().nullable().optional(),
  thumbnail: z.string().nullable().optional(),
});
export type ProductImages = z.infer<typeof ProductImagesSchema>;

export type ProductSupplier = {
  id: string;
  name: string;
  npi: string;
  phoneNumber: string;
  addresses: Address[];
};

/**
 * type Product matches shape of "Product!" type in GraphQL
 *
 * Used for result of queries:
 *
 * getAllMarketplaceProducts
 * searchMarketplaceProducts
 */
export type Product = {
  key: string;
  categories: ProductCategories;
  checkoutProperties: ProductProperties[];
  description: string;
  productId: string;
  uuid: string;
  images: ProductImages[];
  properties: ProductProperties[];
  suppliers: ProductSupplier[];
  title: string;
  variants: ProductVariant[];
  coveredBy: string[];
  version?: number;
  showOnCatalog?: boolean;
};

export const ProductVariantIcdSchema = z.object({
  code: z.string(),
  description: z.string().optional().default(''),
});

const ProductHcpcConfigurationUuidSchema = z.uuid().optional();

export const ProductVariantSchema = z.object({
  key: z.string(),
  productHcpcConfigurationUuid: ProductHcpcConfigurationUuidSchema,
  insurances: z.array(z.string()).optional(),
  attributes: z
    .object({
      hcpc: z.array(z.string().toUpperCase()),
      icds: z.array(ProductVariantIcdSchema).optional(),
    })
    .catchall(z.union([z.string(), z.array(z.string()), z.array(ProductVariantIcdSchema)])),
});
export const NewProductVariantSchema = z.array(
  z.object({
    key: z.string(),
    hcpc: z.array(z.string()).optional(),
    productHcpcConfigurationUuid: ProductHcpcConfigurationUuidSchema,
    attributes: z.array(
      z.object({
        key: z.string(),
        value: z.string(),
      })
    ),
  })
);
export type ProductVariant = z.infer<typeof ProductVariantSchema>;
//export type NewProductVariant = z.infer<typeof NewProductVariantSchema>;

export const ApiProductToProductSchema = z
  .object({
    // WARNING: API products don't include supplier details, or checkoutProperties
    categories: z.object({
      categoryName: z.string(),
      uuid: z.uuid().optional(),
      parentCategory: z
        .object({
          categoryName: z.string(),
          uuid: z.string(),
        })
        .nullable()
        .optional(),
    }),
    description: z.string(),
    images: z.array(
      z.object({
        square: z
          .union([z.object({}), z.string()])
          .nullable()
          .optional()
          .transform((val) => (typeof val === 'string' ? val : '')),
        thumbnail: z
          .union([z.object({}), z.string()])
          .nullable()
          .optional()
          .transform((val) => (typeof val === 'string' ? val : '')),
      })
    ),
    key: z.string(),
    productId: z.string(),
    productUUID: z.string().optional(),
    properties: z.array(
      z.object({
        depends_on: z.string().nullable().optional(),
        display_text: z.string(),
        key: z.string(),
        required: z.boolean(),
        type: z.string(),
        validation_type: z.string().nullable().optional(),
        values: z
          .union([z.array(z.string()), z.record(z.string(), z.array(z.string()))])
          .optional(),
      })
    ),
    suppliers: z.array(z.number()),
    title: z.string(),
    uuid: z.string(),
    variants: z.array(
      z.object({
        attributes: z.record(z.string(), z.any()),
        error: z.any().nullable().optional(),
        key: z.string(),
        product_hcpc_configuration_uuid: z.uuid().nullish(),
        productHcpcConfigurationUuid: z.uuid().nullish(),
      })
    ),
    covered_by: z.array(z.string()).default([]),
  })
  .transform((data): Product => ({
    key: data.key,
    categories: {
      categoryName: data.categories.categoryName,
      parentCategory: data.categories.parentCategory || undefined,
    },
    checkoutProperties: [], // These need to be populated separately if needed
    description: data.description,
    productId: data.productId,
    uuid: data.uuid,
    images: data.images,
    properties: data.properties.map((prop) => ({
      dependsOn: prop.depends_on || null,
      displayText: prop.display_text,
      key: prop.key,
      required: prop.required,
      type: prop.type as any,
      validationType: prop.validation_type || null,
      values: prop.values || [],
      category: '',
      subcategory: null,
    })),
    suppliers: [], // TODO: We can't build suppliers from just IDs :(
    title: data.title,
    variants: data.variants.map((v) => ({
      key: v.key,
      productHcpcConfigurationUuid:
        v.product_hcpc_configuration_uuid ?? v.productHcpcConfigurationUuid ?? undefined,
      attributes: {
        ...v.attributes,
        hcpc: v.attributes.hcpc || [],
      },
    })),
    coveredBy: data.covered_by,
    version: undefined,
    showOnCatalog: undefined,
  }));

// Flexible string input that outputs only non-empty strings or null
const emptyStringSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((val): string | null => {
    if (typeof val !== 'string') return null;
    return val.trim() === '' ? null : val;
  });

const ApiImageLinkSchema = z
  .union([z.object({}), z.url()])
  .transform((val) => (typeof val === 'string' ? val : null));

const ApiPropertySchema = z.object({
  key: z.string(),
  type: z.enum(PROPERTY_TYPES),
  dependsOn: emptyStringSchema,
  displayText: z.string().optional(),
  required: z.boolean(),
  validationType: emptyStringSchema,
  values: z.union([z.array(z.string()), z.record(z.string().toLowerCase(), z.array(z.string()))]),
});

export type ApiProperty = z.infer<typeof ApiPropertySchema>;

const ApiProductSchema = z.object({
  categories: z.object({
    categoryName: z.string(),
    uuid: z.uuid().optional(),
    parentCategory: z
      .object({
        categoryName: z.string(),
        uuid: z.uuid(),
      })
      .nullable()
      .optional()
      .transform((val) => (val ? val : null)),
  }),
  description: z.string(),
  images: z.array(
    z.object({
      square: ApiImageLinkSchema,
      thumbnail: ApiImageLinkSchema,
    })
  ),
  key: z.string(),
  productId: z.string(),
  productUUID: z.uuid(),
  properties: z.array(ApiPropertySchema),
  suppliers: z.array(z.number().int()),
  title: z.string(),
  uuid: z.uuid(),
  variants: z.array(
    z.object({
      attributes: z
        .object({
          hcpc: z.array(z.string().toUpperCase()),
          icds: z.array(ProductVariantIcdSchema).optional(),
        })
        .catchall(z.union([z.string(), z.array(z.string()), z.array(ProductVariantIcdSchema)])),
      error: emptyStringSchema,
      key: z.string(),
      productHcpcConfigurationUuid: z.uuid().optional(),
    })
  ),
});
export type ApiProduct = z.output<typeof ApiProductSchema>;

export const ProductCategoriesListingSchema = z.record(z.string(), z.array(z.string()));
